import React, { useState, useEffect } from 'react';
import { clientComplaintsAPI } from '../lib/api';
import { Loader2 } from 'lucide-react';

const DetailedComplaints = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthlyData, setMonthlyData] = useState({ data: [], grand_total: {} });
  const [yearlyData, setYearlyData] = useState({ data: [], grand_total: {} });

  useEffect(() => {
    const fetchDetailedComplaints = async () => {
      try {
        setLoading(true);
        const response = await clientComplaintsAPI.getDetailedComplaints();
        const data = response.data?.data || response.data;
        
        setMonthlyData(data.monthly || { data: [], grand_total: {} });
        setYearlyData(data.yearly || { data: [], grand_total: {} });
      } catch (err) {
        console.error('Failed to fetch detailed complaints:', err);
        setError('Failed to load complaints data');
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedComplaints();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1b2d4a]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#1b2d4a] text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-4">
            Monthly / Annual Complaints
          </h1>
        </div>
      </section>

      {/* Monthly Complaints Table */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="bg-gray-50 px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                Trend of annual disposal of complaints
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Sr No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Carried forward from previous month
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Received
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Resolved *
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Pending#
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Average Resolution time ^(in days)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {monthlyData.data.map((row) => (
                    <tr key={row.sr_no} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-blue-600">{row.sr_no}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.month}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.carried_forward}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.received}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.resolved}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.pending}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.avg_resolution_time}</td>
                    </tr>
                  ))}
                  {/* Grand Total Row */}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-6 py-4 text-sm text-gray-900"></td>
                    <td className="px-6 py-4 text-sm text-gray-900">Grand Total</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{monthlyData.grand_total?.carried_forward}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{monthlyData.grand_total?.received}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{monthlyData.grand_total?.resolved}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{monthlyData.grand_total?.pending}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{monthlyData.grand_total?.avg_resolution_time || 'N.A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footnotes for Monthly */}
          <div className="bg-blue-50 rounded-lg p-6 mb-16">
            <p className="text-sm text-gray-700 mb-1">
              * Inclusive of complaints of previous months resolved in the current Year.
            </p>
            <p className="text-sm text-gray-700">
              # Inclusive of complaints pending as on the last day of the Year.
            </p>
          </div>

          {/* Annual Complaints Table */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="bg-gray-50 px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                Trend of annual disposal of complaints
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Sr No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Carried forward from previous Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Received
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Resolved *
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Pending#
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {yearlyData.data.map((row) => (
                    <tr key={row.sr_no} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-blue-600">{row.sr_no}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{row.year}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.carried_forward}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.received}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.resolved}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.pending}</td>
                    </tr>
                  ))}
                  {/* Grand Total Row */}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-6 py-4 text-sm text-gray-900"></td>
                    <td className="px-6 py-4 text-sm text-gray-900">Grand Total</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{yearlyData.grand_total?.carried_forward}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{yearlyData.grand_total?.received}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{yearlyData.grand_total?.resolved}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{yearlyData.grand_total?.pending}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footnotes for Annual */}
          <div className="bg-blue-50 rounded-lg p-6">
            <p className="text-sm text-gray-700 mb-1">
              * Inclusive of complaints of previous months resolved in the current Year.
            </p>
            <p className="text-sm text-gray-700">
              # Inclusive of complaints pending as on the last day of the Year.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DetailedComplaints;
